import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Truck, Loader2, Save, MapPin, CheckCircle, XCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const COURIERS = [
  { id: 'jne', name: 'JNE' },
  { id: 'pos', name: 'POS Indonesia' },
  { id: 'tiki', name: 'TIKI' },
  { id: 'jnt', name: 'J&T Express' },
  { id: 'sicepat', name: 'SiCepat' },
  { id: 'anteraja', name: 'AnterAja' },
];

const KOMSHIP_COURIERS = [
  { id: 'jne', name: 'JNE', cod: true },
  { id: 'sap', name: 'SAP Express', cod: true },
  { id: 'idexpress', name: 'ID Express', cod: true },
  { id: 'sicepat', name: 'SiCepat', cod: true },
  { id: 'jnt', name: 'J&T Express', cod: true },
  { id: 'ninja', name: 'Ninja Xpress', cod: true },
  { id: 'lion', name: 'Lion Parcel', cod: false },
  { id: 'gosend', name: 'GoSend', cod: false },
];

export default function ShippingSettings() {
  const [settings, setSettings] = useState({
    provider: 'manual',
    origin_city_id: '',
    origin_province_id: '',
    origin_postal_code: '',
    origin_address: '',
    origin_sender_name: '',
    origin_sender_phone: '',
    free_shipping_min: 0,
    flat_rate: 0,
    enabled_couriers: ['jne', 'pos', 'tiki'],
    rajaongkir_available: false,
    komship_available: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originSearch, setOriginSearch] = useState('');
  const [originResults, setOriginResults] = useState([]);
  const [originSearching, setOriginSearching] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(null);

  useEffect(() => {
    api.get('/tenant/shipping-settings').then(res => {
      setSettings(s => ({ ...s, ...res.data }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/shipping-settings', settings);
      toast.success('Pengaturan pengiriman disimpan');
    } catch (e) {
      toast.error('Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const toggleCourier = (id) => {
    const couriers = settings.enabled_couriers.includes(id)
      ? settings.enabled_couriers.filter(c => c !== id)
      : [...settings.enabled_couriers, id];
    setSettings({ ...settings, enabled_couriers: couriers });
  };

  const searchOrigin = async () => {
    if (!originSearch || originSearch.length < 2) { toast.error('Minimal 2 karakter'); return; }
    setOriginSearching(true);
    try {
      const res = await api.get(`/store/shipping/destination-search?search=${encodeURIComponent(originSearch)}&limit=10`);
      const data = res.data?.data || res.data?.rajaongkir?.results || [];
      setOriginResults(Array.isArray(data) ? data : []);
    } catch { toast.error('Gagal mencari lokasi asal'); }
    finally { setOriginSearching(false); }
  };

  const selectOrigin = (item) => {
    const id = item.subdistrict_id || item.district_id || item.city_id || item.id || '';
    setSettings({
      ...settings,
      origin_city_id: String(id),
      origin_province_id: String(item.province_id || ''),
      origin_postal_code: item.postal_code || '',
    });
    setSelectedOrigin(item);
    setOriginResults([]);
    setOriginSearch('');
  };

  const getOriginLabel = () => {
    if (selectedOrigin) {
      const parts = [selectedOrigin.subdistrict_name, selectedOrigin.district_name || selectedOrigin.city_name, selectedOrigin.province_name || selectedOrigin.province].filter(Boolean);
      return parts.join(', ');
    }
    if (settings.origin_city_id) return `ID: ${settings.origin_city_id}`;
    return null;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin h-8 w-8 text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="shipping-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pengaturan Pengiriman</h1>
          <p className="text-sm text-slate-500">Konfigurasi kalkulasi ongkos kirim untuk toko Anda</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-shipping-btn">
          {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
          Simpan
        </Button>
      </div>

      {/* Provider Selection */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck size={18} className="text-blue-600" /> Metode Kalkulasi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Provider Ongkir</Label>
            <Select value={settings.provider} onValueChange={(v) => setSettings({ ...settings, provider: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual (atur sendiri per order)</SelectItem>
                {settings.rajaongkir_available && <SelectItem value="rajaongkir">RajaOngkir (Kalkulasi Ongkir)</SelectItem>}
                {settings.komship_available && <SelectItem value="komship">Komship (Delivery Order + COD)</SelectItem>}
                <SelectItem value="flat">Flat Rate</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* RajaOngkir status */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border">
            {settings.rajaongkir_available ? (
              <>
                <CheckCircle size={16} className="text-emerald-500" />
                <span className="text-sm text-emerald-700">RajaOngkir aktif — API key dikelola oleh platform superadmin</span>
              </>
            ) : (
              <>
                <XCircle size={16} className="text-slate-400" />
                <span className="text-sm text-slate-500">RajaOngkir belum diaktifkan oleh admin platform. Hubungi admin.</span>
              </>
            )}
          </div>

          {/* Komship status */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border">
            {settings.komship_available ? (
              <>
                <CheckCircle size={16} className="text-blue-500" />
                <span className="text-sm text-blue-700">Komship aktif — Delivery order, COD, & tracking tersedia</span>
              </>
            ) : (
              <>
                <XCircle size={16} className="text-slate-400" />
                <span className="text-sm text-slate-500">Komship belum diaktifkan oleh admin platform.</span>
              </>
            )}
          </div>

          {settings.provider === 'rajaongkir' && settings.rajaongkir_available && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 font-medium">Konfigurasi Lokasi Asal Pengiriman</p>
              <div>
                <Label>Cari Lokasi Asal (kecamatan/kota)</Label>
                <div className="flex gap-2">
                  <Input
                    value={originSearch}
                    onChange={(e) => setOriginSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchOrigin()}
                    placeholder="Ketik nama kota atau kecamatan asal..."
                    data-testid="origin-search-input"
                  />
                  <Button variant="outline" onClick={searchOrigin} disabled={originSearching}>
                    {originSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  </Button>
                </div>
              </div>

              {originResults.length > 0 && (
                <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                  {originResults.map((item, i) => (
                    <button key={i} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-0"
                      onClick={() => selectOrigin(item)}>
                      <span className="font-medium">{item.subdistrict_name || item.district_name || item.city_name}</span>
                      <span className="text-slate-500 ml-1">
                        — {item.district_name || item.city_name}, {item.province_name || item.province}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {getOriginLabel() && (
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Asal: {getOriginLabel()}</span>
                  <Badge variant="secondary" className="text-xs">ID: {settings.origin_city_id}</Badge>
                </div>
              )}
            </div>
          )}

          {/* Komship origin config */}
          {settings.provider === 'komship' && settings.komship_available && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 font-medium">Konfigurasi Pengirim (Sender) untuk Komship</p>
              <div>
                <Label>Cari Lokasi Asal (kecamatan/kota)</Label>
                <div className="flex gap-2">
                  <Input
                    value={originSearch}
                    onChange={(e) => setOriginSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchOrigin()}
                    placeholder="Ketik nama kota atau kecamatan asal..."
                    data-testid="komship-origin-search-input"
                  />
                  <Button variant="outline" onClick={searchOrigin} disabled={originSearching}>
                    {originSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  </Button>
                </div>
              </div>

              {originResults.length > 0 && (
                <div className="border rounded-lg max-h-48 overflow-y-auto bg-white">
                  {originResults.map((item, i) => (
                    <button key={i} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-0"
                      onClick={() => selectOrigin(item)}>
                      <span className="font-medium">{item.subdistrict_name || item.district_name || item.city_name}</span>
                      <span className="text-slate-500 ml-1">— {item.district_name || item.city_name}, {item.province_name || item.province}</span>
                    </button>
                  ))}
                </div>
              )}

              {getOriginLabel() && (
                <div className="flex items-center gap-2 mb-2">
                  <MapPin size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Asal: {getOriginLabel()}</span>
                  <Badge variant="secondary" className="text-xs">ID: {settings.origin_city_id}</Badge>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nama Pengirim</Label>
                  <Input value={settings.origin_sender_name || ''} onChange={(e) => setSettings({ ...settings, origin_sender_name: e.target.value })} placeholder="Nama toko / pengirim" />
                </div>
                <div>
                  <Label>Telepon Pengirim</Label>
                  <Input value={settings.origin_sender_phone || ''} onChange={(e) => setSettings({ ...settings, origin_sender_phone: e.target.value })} placeholder="08xxxxxxxxxx" />
                </div>
              </div>
              <div>
                <Label>Alamat Lengkap Pengirim</Label>
                <Input value={settings.origin_address || ''} onChange={(e) => setSettings({ ...settings, origin_address: e.target.value })} placeholder="Jl. Contoh No. 1, RT/RW, Kelurahan" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Flat Rate & Free Shipping */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin size={18} className="text-emerald-600" /> Ongkir & Gratis Ongkir
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Flat Rate (Rp)</Label>
              <Input type="number" value={settings.flat_rate} onChange={(e) => setSettings({ ...settings, flat_rate: parseInt(e.target.value) || 0 })} placeholder="0 = nonaktif" data-testid="flat-rate-input" />
              <p className="text-xs text-slate-500 mt-1">Ongkir tetap untuk semua pengiriman. 0 = nonaktif.</p>
            </div>
            <div>
              <Label>Gratis Ongkir Min. Pembelian (Rp)</Label>
              <Input type="number" value={settings.free_shipping_min} onChange={(e) => setSettings({ ...settings, free_shipping_min: parseInt(e.target.value) || 0 })} placeholder="0 = nonaktif" data-testid="free-shipping-min-input" />
              <p className="text-xs text-slate-500 mt-1">Minimum pembelian untuk gratis ongkir. 0 = nonaktif.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Courier Selection */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Kurir Tersedia</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {(settings.provider === 'komship' ? KOMSHIP_COURIERS : COURIERS).map(c => (
              <label key={c.id} className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${settings.enabled_couriers.includes(c.id) ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} onClick={() => toggleCourier(c.id)}>
                <input type="checkbox" checked={settings.enabled_couriers.includes(c.id)} readOnly className="h-4 w-4 accent-blue-500" />
                <div>
                  <span className="font-medium text-sm">{c.name}</span>
                  {c.cod !== undefined && <span className={`ml-2 text-xs ${c.cod ? 'text-emerald-600' : 'text-slate-400'}`}>{c.cod ? 'COD ✓' : 'No COD'}</span>}
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
